import { z } from 'zod';
/**
 * Supported UI languages for the driver PWA (the i18n epic). English is the
 * source language and the guaranteed fallback for any un-translated key.
 *
 * Single source of truth: the DB `User.locale` enum, the /me/preferences
 * route schema, the client PreferencesView, the Settings picker, and the
 * i18next config all reference these exports — mirroring how AVATAR_STYLES
 * fans out.
 *
 * NOTE: legal copy (Terms, payout/SP agreements, disclaimers) is NEVER
 * translated — it is versioned, counsel-owned, and audit-trailed. Those
 * bodies stay English regardless of the driver's locale.
 */
export const SupportedLocaleSchema = z.enum([
    'en', // English
    'es', // Spanish
    'fr', // French
    'zh', // Chinese (Simplified)
    'ja', // Japanese
    'ru', // Russian
    'uk', // Ukrainian
    'hy', // Armenian
    'ko', // Korean
    'it', // Italian
    'pt', // Portuguese (Brazilian)
]);
export const SUPPORTED_LOCALES = SupportedLocaleSchema.options;
export const DEFAULT_LOCALE = 'en';
/** Endonym (native name) for each locale — shown in the language picker so a
 *  speaker recognizes their language regardless of the current UI language. */
export const LOCALE_LABELS = {
    en: 'English',
    es: 'Español',
    fr: 'Français',
    zh: '中文（简体）',
    ja: '日本語',
    ru: 'Русский',
    uk: 'Українська',
    hy: 'Հայերեն',
    ko: '한국어',
    it: 'Italiano',
    pt: 'Português',
};
/** Emoji flag per locale, for the language picker (and any future locale
 *  surface). Rendered before the endonym, e.g. "🇰🇷  한국어". Flags follow the
 *  primary region we recognize each language by; pt is Brazilian, matching
 *  LOCALE_BCP47 and the Polly voice locales. */
export const LOCALE_FLAG = {
    en: '🇺🇸',
    es: '🇪🇸',
    fr: '🇫🇷',
    zh: '🇨🇳',
    ja: '🇯🇵',
    ru: '🇷🇺',
    uk: '🇺🇦',
    hy: '🇦🇲',
    ko: '🇰🇷',
    it: '🇮🇹',
    pt: '🇧🇷',
};
/** English name for each locale — used server-side in the EVII "respond in X"
 *  instruction (the model keys off the English language name). */
export const LOCALE_ENGLISH_NAMES = {
    en: 'English',
    es: 'Spanish',
    fr: 'French',
    zh: 'Simplified Chinese',
    ja: 'Japanese',
    ru: 'Russian',
    uk: 'Ukrainian',
    hy: 'Armenian',
    ko: 'Korean',
    it: 'Italian',
    pt: 'Portuguese',
};
/**
 * EVII spoken voice per (locale, gender). `null` means TEXT-ONLY: Polly has no
 * suitable voice, so EVII does NOT speak for that locale. The reply is shown as
 * text and no audio is produced (resolveVoice returns null; the /speak route
 * answers no-audio, never English). Only English (or an absent locale) uses the
 * English voices. Armenian is text-only (no Polly TTS); Ukrainian borrows the
 * Russian voices, which read Cyrillic. Where a language offers a single voice
 * for both genders (Chinese), both keys point at it.
 *
 * Each voiceId is paired with an engine Polly actually offers for it
 * (generative where available, else neural/standard). polly.ts honors per-voice
 * env overrides for English and retries a failed generative call on neural.
 */
export const LOCALE_POLLY_VOICES = {
    en: { female: { voiceId: 'Tiffany', engine: 'generative' }, male: { voiceId: 'Matthew', engine: 'generative' } },
    es: { female: { voiceId: 'Lupe', engine: 'generative' }, male: { voiceId: 'Pedro', engine: 'generative' } },
    fr: { female: { voiceId: 'Ambre', engine: 'generative' }, male: { voiceId: 'Remi', engine: 'generative' } },
    zh: { female: { voiceId: 'Zhiyu', engine: 'neural' }, male: { voiceId: 'Zhiyu', engine: 'neural' } },
    ja: { female: { voiceId: 'Kazuha', engine: 'neural' }, male: { voiceId: 'Takumi', engine: 'neural' } },
    ru: { female: { voiceId: 'Tatyana', engine: 'standard' }, male: { voiceId: 'Maxim', engine: 'standard' } },
    // Ukrainian: no Polly Ukrainian voice; the Russian voices read Cyrillic.
    uk: { female: { voiceId: 'Tatyana', engine: 'standard' }, male: { voiceId: 'Maxim', engine: 'standard' } },
    // Armenian: text-only, no Polly voice.
    hy: { female: null, male: null },
    // Korean: Polly offers only Seoyeon — one voice covers both genders.
    ko: { female: { voiceId: 'Seoyeon', engine: 'generative' }, male: { voiceId: 'Seoyeon', engine: 'generative' } },
    it: { female: { voiceId: 'Beatrice', engine: 'generative' }, male: { voiceId: 'Lorenzo', engine: 'generative' } },
    // Brazilian Portuguese: Camila is generative; Thiago tops out at neural.
    pt: { female: { voiceId: 'Camila', engine: 'generative' }, male: { voiceId: 'Thiago', engine: 'neural' } },
};
/**
 * The distinct EVII voice personas offered for a locale, derived from
 * LOCALE_POLLY_VOICES. Drives the data-driven voice chooser (tour + Settings):
 *   - two entries -> two options (female + male)
 *   - one entry   -> a single voice covers both genders (Chinese) -> ['female']
 *   - none        -> text-only (Armenian) -> no voice chooser
 * The persisted choice stays a gender in User.evie_voice; a single-option
 * locale resolves to 'female'.
 */
export function voiceOptionsForLocale(locale) {
    const row = LOCALE_POLLY_VOICES[locale];
    const f = row?.female;
    const m = row?.male;
    if (!f && !m)
        return [];
    if (f && m)
        return f.voiceId === m.voiceId ? ['female'] : ['female', 'male'];
    return f ? ['female'] : ['male'];
}
/** Coerce an arbitrary string (device locale, stored pref) to a supported
 *  locale, matching on the primary subtag (e.g. `es-MX` -> `es`). Falls back
 *  to English. */
export function normalizeLocale(raw) {
    if (!raw)
        return DEFAULT_LOCALE;
    const primary = raw.toLowerCase().split('-')[0] ?? '';
    return SUPPORTED_LOCALES.includes(primary)
        ? primary
        : DEFAULT_LOCALE;
}
/**
 * BCP-47 language tag per app locale, for the browser Web Speech API's
 * `SpeechRecognition.lang` — the mic INPUT side, mirroring LOCALE_POLLY_VOICES
 * on the TTS OUTPUT side. The app's own locales are bare primary subtags; the
 * recognizer wants a region-qualified tag so it loads the right language model
 * (e.g. `pt` -> `pt-BR`). Region choices track the driver base and the Polly
 * voice locales (es-US, pt-BR).
 */
export const LOCALE_BCP47 = {
    en: 'en-US',
    es: 'es-US',
    fr: 'fr-FR',
    zh: 'zh-CN',
    ja: 'ja-JP',
    ru: 'ru-RU',
    uk: 'uk-UA',
    hy: 'hy-AM',
    ko: 'ko-KR',
    it: 'it-IT',
    pt: 'pt-BR',
};
/**
 * Resolve any locale string (app locale, device locale, stored pref) to the
 * BCP-47 tag for browser speech recognition. Normalizes first (so `pt-PT`,
 * `es-MX`, an absent value, etc. collapse to a supported app locale), then
 * maps. Empty/unknown input normalizes to `en` -> `en-US`.
 */
export function localeToBcp47(locale) {
    return LOCALE_BCP47[normalizeLocale(locale)];
}
//# sourceMappingURL=locale.js.map